import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { inject, observer } from 'mobx-react'
import { Button, Form, Input, message, Modal, Spin, Upload } from 'antd'
import PropTypes from 'prop-types'
import validator from '../../validator'
import { withRouter } from 'react-router-dom'
import RichEditor from '../../components/RichEditor/RichEditor'
import UploadFileItem from '../../components/Common/FileItemComponent/UploadFileItem'
import utils from '../../utils'
import { UploadFileListWrapper } from '../../components/CommentComponent/CommentComponentStyled'
import TinTucConfirmSendNotificationModal from '../TinTucCreatePage/TinTucConfirmSendNotificationModal'
import DraggerUploadButton from '../../components/Common/DraggerUploadButton'
import { NotificationModalGlobalStyle } from './AddEditNotificationModalV2Styled'
import {
  getTinTucErrorMessage,
  logTinTucErrorInDev,
} from './tinTucErrorHelper'

const AddEditTinTucModalV2 = propsAddEditTinTucModalV2 => {
  const {
    authenticationStore,
    fileStore,
    onRefresh,
    open,
    selectedNotificationId,
    setOpen,
    tintucStore,
  } = propsAddEditTinTucModalV2

  const [notificationDetail, setNotificationDetail] = useState(null)

  const EDITOR_REF = useRef()
  const [form] = Form.useForm()

  const [fileList, setFileList] = useState([])
  const [fileListUpdate, setFileListUpdate] = useState([])
  const [editorContent, setEditorContent] = useState(null)
  const [
    isCofirmSendNotificationModalVisible,
    setConfirmSendNotificationModalVisibility,
  ] = useState(false)

  const [updateValues, setUpdateValues] = useState(null)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const modalTitle = useMemo(
    () => (selectedNotificationId ? 'Cập nhật thông báo' : 'Tạo thông báo mới'),
    [selectedNotificationId]
  )

  const closeModal = useCallback(() => {
    if (isSubmitting) return
    setOpen(false)
  }, [isSubmitting, setOpen])

  const confirmUpdate = async ({ is_notification_required }) => {
    setConfirmSendNotificationModalVisibility(false)
    try {
      setIsSubmitting(true)
      await tintucStore.update({
        ...updateValues,
        is_notification_required,
      })
      message.success('Sửa thông báo thành công!')
      setOpen(false)
      onRefresh && onRefresh()
    } catch (err) {
      logTinTucErrorInDev(err, 'AddEditTinTucModalV2.confirmUpdate')
      message.error(
        getTinTucErrorMessage(err, 'Không thể cập nhật thông báo!')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChangeFile = useCallback(
    info => {
      if (info.fileList.length === 0) {
        form.setFieldsValue({ files: undefined })
        setFileList([])
        return
      }
      const filteredFileList = info.fileList.filter(
        (elem, index, fileList) =>
          fileList.findIndex(file => file.name === elem.name) === index
      )
      setFileList(filteredFileList)
    },
    [form]
  )

  const handleRemoveFileFromUploadList = useCallback(
    fileUID => {
      const newFileList = fileList.filter(file => file.uid !== fileUID)
      setFileList(newFileList)
    },
    [fileList]
  )

  const handleRemoveFileFromListUpdate = useCallback(
    file_id => {
      const newFileListUpdate = fileListUpdate.filter(
        file => file.file_id !== file_id
      )
      setFileListUpdate(newFileListUpdate)
    },
    [fileListUpdate]
  )

  const onFinish = async values => {
    setIsSubmitting(true)

    const uploadFileList = fileList
      ? fileList.map(file => file.originFileObj)
      : []
    const batchUploadArr = []
    uploadFileList.forEach(file => {
      const formData = new FormData()
      formData.append('file', file)
      batchUploadArr.push(fileStore.uploadFile(formData))
    })
    const fileListUpdateOrigin =
      notificationDetail &&
      notificationDetail?.attachments?.map(file => file.file_id)
    const fileListUpdateAfter =
      fileListUpdate && fileListUpdate?.map(file => file.file_id)

    try {
      const response = await Promise.all(batchUploadArr)
      let submitValues = {
        ...values,
        id: null,
        attachments_request: {
          new_items: response.map(res => res.data.file_id),
        },
        content: EDITOR_REF?.current?.editor?.getData(),
        author: {
          name_lowercase: authenticationStore.currentUser.name_lowercase,
          user_name: authenticationStore.currentUser.username,
        },
      }
      if (selectedNotificationId) {
        setUpdateValues({
          ...submitValues,
          id: selectedNotificationId,
          attachments_request: {
            new_items: response.map(res => res.data.file_id),
            remove_items: fileListUpdateOrigin?.filter(
              file => !fileListUpdateAfter.includes(file)
            ),
          },
        })
        setIsSubmitting(false)
        setConfirmSendNotificationModalVisibility(true)
        return
      }
      if (!selectedNotificationId) {
        await tintucStore.create(submitValues)
        message.success('Tạo thông báo thành công!')
        setOpen(false)
        onRefresh && onRefresh()
      }
    } catch (err) {
      logTinTucErrorInDev(err, 'AddEditTinTucModalV2.onFinish')
      message.error(
        getTinTucErrorMessage(
          err,
          selectedNotificationId
            ? 'Không thể cập nhật thông báo!'
            : 'Không thể tạo thông báo!'
        )
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderFileList = fileList.length !== 0 && (
    <UploadFileListWrapper style={{ marginTop: 12 }}>
      {fileList.map(file => (
        <UploadFileItem
          key={file.uid}
          file_id={file.uid}
          file_name={file.name}
          file_type={utils.getExtensionFile(file.name)}
          handleRemoveFileFromUploadList={() =>
            handleRemoveFileFromUploadList(file.uid)
          }
        />
      ))}
    </UploadFileListWrapper>
  )
  const renderFileListUpdate = fileListUpdate && (
    <UploadFileListWrapper style={{ marginTop: 12 }}>
      {fileListUpdate.map(file => (
        <UploadFileItem
          key={file.file_id}
          file_id={file.file_id}
          file_name={file.file_name}
          file_type={utils.getExtensionFile(file.file_name)}
          isDownloadFile={true}
          handleRemoveFileFromUploadList={() =>
            handleRemoveFileFromListUpdate(file.file_id)
          }
        />
      ))}
    </UploadFileListWrapper>
  )

  useEffect(() => {
    if (!open || !selectedNotificationId) {
      setNotificationDetail(null)
      setEditorContent(null)
      setFileList([])
      setFileListUpdate([])
      form.resetFields()
      return
    }
    ;(async () => {
      try {
        setIsLoadingDetail(true)
        const res = await tintucStore.getById(selectedNotificationId)
        setNotificationDetail(res.data)
      } catch (error) {
        logTinTucErrorInDev(error, 'AddEditTinTucModalV2.loadDetail')
        message.error(
          getTinTucErrorMessage(error, 'Không thể tải thông tin thông báo!')
        )
      } finally {
        setIsLoadingDetail(false)
      }
    })()
  }, [form, open, selectedNotificationId, tintucStore])

  useEffect(() => {
    if (!notificationDetail) return
    setEditorContent(notificationDetail?.content)
    setFileListUpdate(notificationDetail?.attachments || [])
    form.setFieldsValue({
      subject: notificationDetail?.subject,
    })
  }, [notificationDetail, form])

  return (
    <>
      <NotificationModalGlobalStyle />
      <Modal
        style={{ top: 32 }}
        title={modalTitle}
        visible={open}
        onCancel={closeModal}
        footer={null}
        wrapClassName="notification-modal-wrapper"
        width={860}
        destroyOnClose
        maskClosable={!isSubmitting}>
        <TinTucConfirmSendNotificationModal
          visible={isCofirmSendNotificationModalVisible}
          onCancel={() => setConfirmSendNotificationModalVisibility(false)}
          onConfirm={confirmUpdate}
        />
        <Spin spinning={isLoadingDetail}>
          <Form
            scrollToFirstError={true}
            form={form}
            onFinish={onFinish}
            layout="vertical">
            <Form.Item
              name={'subject'}
              label={'Tiêu đề'}
              rules={[
                { required: true, message: 'Vui lòng nhập tiêu đề!' },
                { validator: validator.validateInputString },
              ]}>
              <Input placeholder={'Nhập tiêu đề thông báo'} />
            </Form.Item>
            <Form.Item label={'Nội dung'} name={'content'}>
              <div className="notification-editor-wrapper">
                <RichEditor
                  EDITOR_REF={EDITOR_REF}
                  placeholder={'Nhập nội dung thông báo'}
                  editorContent={editorContent}
                />
              </div>
            </Form.Item>
            <Form.Item label={'Tài liệu đính kèm'} name={'files'}>
              <Upload.Dragger
                valuePropName={'fileList'}
                fileList={fileList}
                onChange={handleChangeFile}
                beforeUpload={() => false}
                multiple={true}
                showUploadList={false}
                className="notification-dragger-upload">
                <DraggerUploadButton />
              </Upload.Dragger>
              {renderFileList}
              {renderFileListUpdate}
            </Form.Item>

            <div className="notification-modal-footer">
              <Button
                type="default"
                onClick={closeModal}
                htmlType="button"
                disabled={isSubmitting}
                className="btn-cancel">
                Hủy bỏ
              </Button>
              <Button
                htmlType={'submit'}
                type={'primary'}
                loading={isSubmitting}
                disabled={isLoadingDetail}
                className="btn-submit">
                {selectedNotificationId ? 'Cập nhật' : 'Đăng thông báo'}
              </Button>
            </div>
          </Form>
        </Spin>
      </Modal>
    </>
  )
}

AddEditTinTucModalV2.propTypes = {
  authenticationStore: PropTypes.object,
  fileStore: PropTypes.object,
  onRefresh: PropTypes.func,
  open: PropTypes.bool,
  selectedNotificationId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  setOpen: PropTypes.func,
  tintucStore: PropTypes.object,
}

export default memo(
  withRouter(
    inject(
      'fileStore',
      'authenticationStore',
      'tintucStore'
    )(observer(AddEditTinTucModalV2))
  )
)
